import {
  Box, Button, CircularProgress, Grid, Modal, Stack,
} from '@mui/material';
import React, { useMemo } from 'react';

type ButtonColor = 'secondary' | 'inherit' | 'primary' | 'success' | 'error' | 'info' | 'warning';

interface Props {
  title: string;
  subTitle?: string;
  body?: any | string;
  cancelButtonText?: string;
  cancelButtonColor?: ButtonColor;
  confirmButtonText?: string;
  confirmButtonColor?: ButtonColor;
  extraButton?: React.ReactNode;
  show: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children?: React.ReactNode;
  closeOnConfirm?: boolean;
  loading?: boolean;
  errorMessage?: string;
  onConfirm: () => void;
  closeModal: () => void;
}

const ModalComponent: React.FC<Props> = ({
  title, subTitle, body, cancelButtonText = 'キャンセル', cancelButtonColor = 'primary',
  confirmButtonText = 'OK', confirmButtonColor = 'primary', onConfirm, show,
  closeModal, size = 'auto', children, closeOnConfirm = true, loading = false, errorMessage = '', extraButton,
}) => {
  const handleClose = (event: React.MouseEvent) => {
    closeModal();
    event.stopPropagation();
  };
  const handleConfirm = (event: React.MouseEvent) => {
    if (closeOnConfirm) {
      handleClose(event);
    }
    onConfirm();
  };

  const modalStyle = useMemo(() => {
    let formattedSize;
    if (size === 'sm') {
      formattedSize = '30%';
    } else if (size === 'md') {
      formattedSize = '50%';
    } else if (size === 'lg') {
      formattedSize = '70%';
    } else if (size === 'xl') {
      formattedSize = '90%';
    } else if (size === 'auto') {
      formattedSize = 'auto';
    }
    const style = {
      position: 'absolute',
      overflow: 'hidden',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: formattedSize,
      maxWidth: '90%',
      maxHeight: '90%',
      height: formattedSize,
      bgcolor: 'background.paper',
      border: '1px solid #000',
      boxShadow: 24,
      p: 2,
    };
    return style;
  }, [size]);

  return (
    <Modal className={`modal-${size}`} open={show} onClose={handleClose}>
      <Box sx={modalStyle} onClick={(event) => event.stopPropagation()}>

        <Stack
          direction="row"
          justifyContent="start"
          alignItems="center"
          sx={{
            height: '10%',
          }}
        >
          <Grid container direction="column">
            <Grid item xs="auto">
              {title}
            </Grid>
            <Grid item xs="auto">
              {subTitle}
            </Grid>
          </Grid>
        </Stack>

        <Box sx={{ height: '80%', overflow: 'auto', p: 2 }}>

          <div className="text-danger">
            {errorMessage}
          </div>
          {body}
          {children}
        </Box>

        <Stack
          direction="row"
          justifyContent="end"
          alignItems="center"
          spacing={2}
          sx={{
            height: '10%',
          }}
        >
          <Button disabled={loading} color={cancelButtonColor} variant="outlined" onClick={handleClose}>
            {cancelButtonText}
          </Button>
          <Button disabled={loading} color={confirmButtonColor} variant="contained" onClick={handleConfirm}>
            {confirmButtonText}
            {loading ? <CircularProgress sx={{ marginLeft: 1 }} color="inherit" size={15} /> : null}
          </Button>
          {extraButton}
        </Stack>
      </Box>
    </Modal>
  );
};

export default ModalComponent;
